import { getSchemaNodesFromUnion } from "src/jsonSchema/schemaUtils";
import {
  isNullType,
  isRealUnion,
  isUnion,
  JsonSchema,
  JsonSchemaNode,
  JsonSchemaType,
} from "src/jsonSchema/types";

export type JsonSchemaNodeTransformer = (
  node: JsonSchemaNode,
  path: string,
) => JsonSchemaNode | undefined;

export type SchemaNormalizer = (schema: JsonSchema) => JsonSchema;

export const normalizeJsonSchema = (
  schema: JsonSchema,
  normalizers: SchemaNormalizer[] = [],
): JsonSchema => {
  let normalizedSchema = { ...schema };

  for (const normalizer of normalizers) {
    normalizedSchema = normalizer(normalizedSchema);
  }

  return normalizedSchema;
};

// - Custom normalizers

/**
 * Converts old-style nullable unions (anyOf/oneOf with single type + null)
 * into modern nullable format (type: ["type", "null"])
 */
export const normalizeNullableUnions: SchemaNormalizer = (
  schema: JsonSchema,
): JsonSchema => {
  const transformer: JsonSchemaNodeTransformer = (
    node: JsonSchemaNode,
  ): JsonSchemaNode | undefined => {
    if (!isUnion(node) || isRealUnion(node)) {
      return;
    }

    const members = getSchemaNodesFromUnion(node);
    if (members.length !== 2) {
      return;
    }
    const nullMembers = members.filter(isNullType);
    const nonNullMembers = members.filter((m) => !isNullType(m));
    if (nullMembers.length !== 1 || nonNullMembers.length !== 1) {
      return;
    }
    const nonNullMember = nonNullMembers[0];

    if (!("type" in nonNullMember)) {
      return; // keep unchanged
    }

    // Construct types array
    let newTypes: JsonSchemaType[] = [];
    if (typeof nonNullMember.type === "string") {
      newTypes = [nonNullMember.type, "null"];
    } else if (Array.isArray(nonNullMember.type)) {
      newTypes = [...nonNullMember.type.filter((t) => t != "null"), "null"];
    }

    // Preserve any other properties from the original union node
    // (except anyOf/oneOf which we're replacing)
    const { anyOf: _anyOf, oneOf: _oneOf, ...otherProps } = node;

    return {
      ...otherProps,
      ...nonNullMember,
      type: newTypes,
    };
  };

  return transformSchema(schema, transformer, "#");
};

// - Traversal helper

export const transformSchema = (
  schema: JsonSchema,
  visitorFunc: JsonSchemaNodeTransformer,
  initialPath = "#",
): JsonSchema => {
  const traverse = (obj: unknown, currentPath: string): unknown => {
    if (!obj || typeof obj !== "object") {
      return obj;
    }

    const transformed = visitorFunc(obj as JsonSchemaNode, currentPath);
    const resultNode = transformed !== undefined ? transformed : obj;

    // If it's an array, traverse each item
    if (Array.isArray(resultNode)) {
      return resultNode.map((item, index) =>
        traverse(item, `${currentPath}/${index}`),
      );
    }

    // For objects, recurse on each property
    const result: Record<string, unknown> = {};
    for (const key in resultNode) {
      const value = (resultNode as Record<string, unknown>)[key];
      if (typeof value !== "object" || value === null) {
        result[key] = value; // copy as-is
      } else {
        result[key] = traverse(value, `${currentPath}/${key}`);
      }
    }

    return result;
  };

  return traverse(schema, initialPath) as JsonSchema;
};
